import { z } from 'zod'
import { zId, zIsoDate, zJsonBlob } from '../common'

export const zTenantListItem = z.object({
  id: zId,
  name: z.string(),
  slug: z.string(),
  language: z.string(),
  defaultAuthor: z.string().nullable(),
  contentPath: z.string().nullable(),
  autoPublish: z.boolean(),
  monthlyCostCapCents: z.number().nullable(),
  status: z.string(),
  createdAt: zIsoDate,
  updatedAt: zIsoDate,
})
export type TenantListItem = z.infer<typeof zTenantListItem>

export const zTenantDetail = zTenantListItem.extend({
  persona: zJsonBlob.nullable(),
  // Autonomous SEO/GEO research config. `gscConfigured` is read-only (computed
  // server-side); `gscCredentialsJson` is write-only and never returned.
  siteUrl: z.string().nullable(),
  gscProperty: z.string().nullable(),
  researchEnabled: z.boolean(),
  gscConfigured: z.boolean(),
  // DataForSEO readiness flags (read-only, computed server-side). Defaulted so
  // older backends without Waves 5–6 still parse.
  dataforseoConfigured: z.boolean().default(false),
  dataforseoEnabled: z.boolean().default(false),
  dataforseoMentionsEnabled: z.boolean().default(false),
  seoSettings: zJsonBlob.nullable(),
  // Public content-API access. `allowedOrigins` are the exact CORS origins that
  // may read this tenant's articles from a browser; `contentApiKeyConfigured` is
  // read-only (the cak_ key itself is only ever returned on rotation). Defaulted
  // so the tenant page keeps rendering against a backend that hasn't shipped
  // these fields yet.
  allowedOrigins: z.array(z.string()).default([]),
  contentApiKeyConfigured: z.boolean().default(false),
  // Multilingual config. `additionalLanguages` are the ISO-639-1 codes auto-
  // translated on every publish; `hreflangXDefault` is the language served as
  // the hreflang x-default (defaults server-side to the tenant `language`);
  // `urlTemplate` is the absolute-URL gabarit (must contain `{slug}`, `{lang}`
  // optional) required for non-null hreflang URLs. Read-only quality flags
  // (verified against prod): `languagePackCurated` is a BOOLEAN for the tenant's
  // default language, and `additionalLanguagesCurated` is the per-target-language
  // map (`{ en: true }`). All defaulted for forward-compat with older payloads.
  additionalLanguages: z.array(z.string()).default([]),
  hreflangXDefault: z.string().nullable().default(null),
  urlTemplate: z.string().nullable().default(null),
  languagePackCurated: z.boolean().default(false),
  additionalLanguagesCurated: z.record(z.boolean()).default({}),
})
export type TenantDetail = z.infer<typeof zTenantDetail>

// The content-API read key is returned in clear exactly ONCE, on rotation, then
// never again — the same one-time-reveal contract as the webhook secret.
export const zTenantContentApiKey = z.object({
  contentApiKey: z.string(),
  tenant: zTenantDetail,
})
export type TenantContentApiKey = z.infer<typeof zTenantContentApiKey>
